import { zodResolver } from '@hookform/resolvers/zod';
import { type RegisterDto, RegisterSchema } from '@nestjs-boilerplate/types';
import { Link, useNavigate } from '@tanstack/react-router';
import { Check, Eye, EyeOff, X } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import logo from '@/assets/OKYC logo.webp';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { useRegister } from '../hooks/use-register';

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    label: 'Be at least 8 characters long',
    test: (password) => password.length >= 8,
  },
  {
    label: 'Be a mixture of letters and numbers',
    test: (password) => /[a-zA-Z]/.test(password) && /[0-9]/.test(password),
  },
  {
    label: 'Contain at least 1 upper case letter',
    test: (password) => /[A-Z]/.test(password),
  },
];

export function RegisterForm() {
  const navigate = useNavigate();
  const { mutate: register, isPending } = useRegister();
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<RegisterDto>({
    resolver: zodResolver(RegisterSchema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onChange',
  });

  const password = form.watch('password');

  function onSubmit(data: RegisterDto) {
    register(data, {
      onSuccess: () => {
        // Navigate to account setup step 2
        navigate({ to: '/account-setup' });
      },
    });
  }

  return (
    <Card className="w-full max-w-md rounded-[5px] border border-[#D4DAE0]">
      <CardHeader className="space-y-4 text-center">
        <img src={logo} alt="OKYC Logo" className="mx-auto h-16 w-auto" />

        <div className="space-y-2">
          <h1 className="text-[20px] font-semibold leading-[120%] text-[#051923]">
            Create Your Account
          </h1>
          <p className="text-[16px] font-normal leading-[22px] text-[#051923]">
            Welcome to Optimal KYC.
          </p>
        </div>

        <p className="text-[16px] font-normal leading-[22px] text-[#051923]">
          Take Anti Money Laundering compliance to the next level with AI PEP & Sanctions Screening
          and automated Adverse Media Investigation.
        </p>
      </CardHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="john.smith@company.com"
                      autoComplete="email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="************"
                        autoComplete="new-password"
                        className="pr-10"
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Password Requirements */}
            <div className="space-y-2">
              <p className="text-[14px] font-medium text-[#051923]">Password must:</p>
              <ul className="space-y-1">
                {PASSWORD_REQUIREMENTS.map((requirement) => {
                  const isMet = requirement.test(password || '');
                  return (
                    <li key={requirement.label} className="flex items-center gap-2">
                      {isMet ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <X className="h-4 w-4 text-gray-400" />
                      )}
                      <span className={`text-[14px] ${isMet ? 'text-green-600' : 'text-gray-500'}`}>
                        {requirement.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              className="h-[45px] w-full rounded-[5px] bg-[#131313] text-[18px] font-medium leading-[100%] text-white transition-opacity duration-300 ease-out hover:bg-[#131313]/90"
              disabled={isPending}
            >
              {isPending ? 'Creating account...' : 'Next'}
            </Button>

            <p className="text-center text-[16px] font-normal leading-[22px] text-[#051923]">
              Already have an account?{' '}
              <Link to="/login" className="font-semibold leading-[140%] text-[#131313] underline">
                Login
              </Link>
            </p>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
}
